/**
 * Chart store for OHLC data management
 * Simplified version with core functionality only
 */

import { create } from "zustand";
import type { Symbol } from "@/vybe-trader-client/symbols/types";
import { aggregateOHLCData } from "./aggregation";

export type TimeFrame =
  | "1m"
  | "5m"
  | "15m"
  | "30m"
  | "1h"
  | "2h"
  | "4h"
  | "12h"
  | "1d"
  | "1w"
  | "1mn";

export interface OHLCData {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface OHLCRequest {
  symbol: string;
  period: number;
  customFrom: string;
  customTo: string;
}

export interface OHLCBar {
  time: string;
  openPrice: number;
  highPrice: number;
  lowPrice: number;
  closePrice: number;
  tickVolume: number;
}

export interface OHLCBatchResponse {
  success: boolean;
  symbol: string;
  bars: OHLCBar[];
  totalBars: number;
  batchCount: number;
  isComplete: boolean;
  errorMessage: string | null;
  from?: string;
  to?: string;
}

interface ChartStore {
  // State
  selectedSymbol: Symbol | null;
  selectedTimeframe: TimeFrame;
  ohlcData: OHLCData[];
  lastBatch: OHLCData[] | null;
  loadingStatus: "idle" | "loading" | "complete" | "error";
  errorMessage: string | null;
  isReadyForChart: boolean;
  isRequestingMoreData: boolean;
  pendingBatchData: OHLCData[]; // Temporary storage for incomplete batches

  // Actions
  selectSymbol: (symbol: Symbol | null) => Promise<void>;
  clearSelection: () => void;
  setTimeframe: (timeframe: TimeFrame) => void;
  processBatch: (batch: OHLCBatchResponse) => void;
  requestOHLCInitializationData: () => Promise<void>;
  requestMoreOHLCData: (fromTimestamp: number) => Promise<void>;
  getAggregatedData: (timeframe?: TimeFrame) => OHLCData[];
  getLastBatchAggregatedData: () => OHLCData[];
  isLoading: () => boolean;
  hasData: () => boolean;
}

// Efficient data extraction for aggregation
const getDataForAggregation = (
  ohlcData: OHLCData[],
  timeframe: TimeFrame,
  maxCandles: number = 500
): OHLCData[] => {
  if (ohlcData.length === 0) return [];

  // For 1m timeframe, just return the last maxCandles
  if (timeframe === "1m") {
    return ohlcData.slice(-maxCandles);
  }

  // Calculate how many raw candles we need for the timeframe
  const multiplier =
    timeframe === "5m"
      ? 5
      : timeframe === "15m"
      ? 15
      : timeframe === "30m"
      ? 30
      : timeframe === "1h"
      ? 60
      : timeframe === "2h"
      ? 120
      : timeframe === "4h"
      ? 240
      : timeframe === "12h"
      ? 720
      : timeframe === "1d"
      ? 1440
      : timeframe === "1mn"
      ? 43200 // 30 days * 24h * 60m
      : 1;
  const neededRawCandles = maxCandles * multiplier;

  // Get the last N raw candles, but ensure we start at a timeframe boundary
  const recentData = ohlcData.slice(-neededRawCandles - multiplier); // Extra buffer for alignment

  if (recentData.length === 0) return [];

  // Find the first candle that aligns with the timeframe boundary
  let startIndex = 0;
  for (let i = 0; i < recentData.length; i++) {
    const date = new Date(recentData[i].timestamp);
    let isAligned = false;

    switch (timeframe) {
      case "5m":
        isAligned = date.getMinutes() % 5 === 0 && date.getSeconds() === 0;
        break;
      case "15m":
        isAligned = date.getMinutes() % 15 === 0 && date.getSeconds() === 0;
        break;
      case "30m":
        isAligned = date.getMinutes() % 30 === 0 && date.getSeconds() === 0;
        break;
      case "1h":
        isAligned = date.getMinutes() === 0 && date.getSeconds() === 0;
        break;
      case "2h":
        isAligned =
          date.getHours() % 2 === 0 &&
          date.getMinutes() === 0 &&
          date.getSeconds() === 0;
        break;
      case "4h":
        isAligned =
          date.getHours() % 4 === 0 &&
          date.getMinutes() === 0 &&
          date.getSeconds() === 0;
        break;
      case "12h":
        isAligned =
          date.getHours() % 12 === 0 &&
          date.getMinutes() === 0 &&
          date.getSeconds() === 0;
        break;
      case "1d":
        isAligned =
          date.getHours() === 0 &&
          date.getMinutes() === 0 &&
          date.getSeconds() === 0;
        break;
      case "1mn":
        isAligned =
          date.getDate() === 1 &&
          date.getHours() === 0 &&
          date.getMinutes() === 0 &&
          date.getSeconds() === 0;
        break;
    }

    if (isAligned) {
      startIndex = i;
      break;
    }
  }

  return recentData.slice(startIndex);
};
const getFullCandleTimestamp = (date: Date, timeframe: TimeFrame): Date => {
  const result = new Date(date);

  switch (timeframe) {
    case "1m":
      result.setSeconds(59, 999);
      break;
    case "5m":
      const minutes5 = Math.floor(result.getMinutes() / 5) * 5 + 4;
      result.setMinutes(minutes5, 59, 999);
      break;
    case "15m":
      const minutes15 = Math.floor(result.getMinutes() / 15) * 15 + 14;
      result.setMinutes(minutes15, 59, 999);
      break;
    case "30m":
      const minutes30 = Math.floor(result.getMinutes() / 30) * 30 + 29;
      result.setMinutes(minutes30, 59, 999);
      break;
    case "1h":
      result.setMinutes(59, 59, 999);
      break;
    case "2h":
      const hours2 = Math.floor(result.getHours() / 2) * 2 + 1;
      result.setHours(hours2, 59, 59, 999);
      break;
    case "4h":
      const hours4 = Math.floor(result.getHours() / 4) * 4 + 3;
      result.setHours(hours4, 59, 59, 999);
      break;
    case "12h":
      const hours12 = Math.floor(result.getHours() / 12) * 12 + 11;
      result.setHours(hours12, 59, 59, 999);
      break;
    case "1d":
      result.setHours(23, 59, 59, 999);
      break;
    case "1mn":
      // Last day of month, 23:59:59
      result.setMonth(result.getMonth() + 1, 0); // Go to last day of current month
      result.setHours(23, 59, 59, 999);
      break;
  }

  return result;
};

// Helper function to get minimum candles required for timeframe
const getMinimumCandlesForTimeframe = (timeframe: TimeFrame): number => {
  // Always require 50 candles for processBatch to update lastBatch
  return 50;
};

// Helper function to get maximum candles for batch accumulation
const getMaximumCandlesForTimeframe = (timeframe: TimeFrame): number => {
  // Always require 50 candles for processBatch to update lastBatch
  return 50;
};

// Helper function to calculate candles back in time
const calculateFromTimestamp = (
  toDate: Date,
  candlesBack: number,
  timeframe: TimeFrame
): Date => {
  const fromDate = new Date(toDate);

  switch (timeframe) {
    case "1m":
      fromDate.setMinutes(fromDate.getMinutes() - candlesBack);
      break;
    case "5m":
      fromDate.setMinutes(fromDate.getMinutes() - candlesBack * 5);
      break;
    case "15m":
      fromDate.setMinutes(fromDate.getMinutes() - candlesBack * 15);
      break;
    case "30m":
      fromDate.setMinutes(fromDate.getMinutes() - candlesBack * 30);
      break;
    case "1h":
      fromDate.setHours(fromDate.getHours() - candlesBack);
      break;
    case "2h":
      fromDate.setHours(fromDate.getHours() - candlesBack * 2);
      break;
    case "4h":
      fromDate.setHours(fromDate.getHours() - candlesBack * 4);
      break;
    case "12h":
      fromDate.setHours(fromDate.getHours() - candlesBack * 12);
      break;
    case "1d":
      fromDate.setDate(fromDate.getDate() - candlesBack);
      break;
    case "1mn":
      fromDate.setMonth(fromDate.getMonth() - candlesBack);
      break;
  }

  return fromDate;
};

export const useChartStore = create<ChartStore>((set, get) => ({
  // Initial state
  selectedSymbol: null,
  selectedTimeframe: "1m",
  ohlcData: [],
  lastBatch: null,
  loadingStatus: "idle",
  errorMessage: null,
  isReadyForChart: false,
  isRequestingMoreData: false,
  pendingBatchData: [],

  // Clear the current symbol selection and reset data
  clearSelection: () => {
    set({
      selectedSymbol: null,
      ohlcData: [],
      lastBatch: null,
      loadingStatus: "idle",
      errorMessage: null,
      isReadyForChart: false,
      isRequestingMoreData: false,
      pendingBatchData: [],
    });
  },

  // Getters
  isLoading: () => get().loadingStatus === "loading",
  hasData: () => get().ohlcData.length > 0,
  getLastBatchAggregatedData: () => get().lastBatch ?? [],

  // Select symbol - clears ohlcData and lastBatch
  selectSymbol: async (symbol: Symbol | null) => {
    const state = get();

    // If same symbol, do nothing
    if (state.selectedSymbol?.currency === symbol?.currency) {
      return;
    }

    // Clear data when changing symbol
    set({
      selectedSymbol: symbol,
      ohlcData: [],
      lastBatch: null,
      loadingStatus: symbol ? "loading" : "idle",
      isReadyForChart: false,
      isRequestingMoreData: false,
      pendingBatchData: [],
    });

    // Request initial data if symbol is selected
    if (symbol) {
      await get().requestOHLCInitializationData();
    }
  },

  // Set timeframe - now triggers data aggregation
  setTimeframe: (timeframe: TimeFrame) => {
    const state = get();

    // Don't reset if same timeframe
    if (state.selectedTimeframe === timeframe) {
      return;
    }

    // If we have data, immediately aggregate it for the new timeframe
    if (state.ohlcData.length > 0) {
      const dataForAggregation = getDataForAggregation(
        state.ohlcData, // Use current state, not get()
        timeframe,
        500
      );

      if (dataForAggregation.length > 0) {
        const aggregatedData = aggregateOHLCData(dataForAggregation, timeframe);

        // Single set() with all changes
        set({
          selectedTimeframe: timeframe,
          isReadyForChart: aggregatedData.length > 0,
          lastBatch: aggregatedData,
          pendingBatchData: [],
        });
      } else {
        // No data to aggregate
        set({
          selectedTimeframe: timeframe,
          isReadyForChart: false,
          lastBatch: null,
          pendingBatchData: [],
        });
      }
    } else {
      // No raw data available
      set({
        selectedTimeframe: timeframe,
        isReadyForChart: false,
        lastBatch: null,
        pendingBatchData: [],
      });
    }
  },

  // Process incoming batch
  processBatch: (batch: OHLCBatchResponse) => {
    const state = get();

    // Only process if it's for the currently selected symbol
    if (
      !state.selectedSymbol ||
      batch.symbol !== state.selectedSymbol.currency
    ) {
      return;
    }

    if (!batch.success) {
      set({ loadingStatus: "error", errorMessage: batch.errorMessage });
      console.error("[Chart] Batch processing failed:", batch.errorMessage);
      return;
    }

    // Convert bars to OHLCData format (keep timestamps as is - they come in UTC)
    const newData: OHLCData[] = batch.bars.map((bar) => ({
      timestamp: new Date(bar.time).getTime(),
      open: bar.openPrice,
      high: bar.highPrice,
      low: bar.lowPrice,
      close: bar.closePrice,
      volume: bar.tickVolume,
    }));

    // Merge with existing data
    const mergedData = [...newData, ...state.ohlcData];

    // Remove duplicates and sort by timestamp
    const uniqueData = Array.from(
      new Map(mergedData.map((item) => [item.timestamp, item])).values()
    ).sort((a, b) => a.timestamp - b.timestamp);

    // Combine with pending batch data
    const combinedPendingData = [...newData, ...state.pendingBatchData];

    // Remove duplicates from pending data
    const uniquePendingData = Array.from(
      new Map(
        combinedPendingData.map((item) => [item.timestamp, item])
      ).values()
    ).sort((a, b) => a.timestamp - b.timestamp);

    // Use efficient aggregation for pending data - ensure we get complete candles
    const dataForAggregation = getDataForAggregation(
      uniquePendingData,
      state.selectedTimeframe,
      200 // Allow more for initial loading
    );

    // Aggregate the efficiently selected data based on current timeframe
    const aggregatedData = aggregateOHLCData(
      dataForAggregation,
      state.selectedTimeframe
    );

    // Get minimum and maximum candles requirements based on timeframe
    const minCandles = getMinimumCandlesForTimeframe(state.selectedTimeframe);
    const maxCandles = getMaximumCandlesForTimeframe(state.selectedTimeframe);

    // For historical data (LoadMore), always update lastBatch if we have minimum candles
    // For initial loading, use the original logic
    const isHistoricalData =
      newData.length > 0 &&
      state.lastBatch &&
      state.lastBatch.length > 0 &&
      newData[newData.length - 1].timestamp < state.lastBatch[0].timestamp;

    const requiredCandles = isHistoricalData
      ? minCandles
      : state.lastBatch && state.lastBatch.length > 0
      ? maxCandles
      : minCandles;

    // Check if we have enough candles
    if (aggregatedData.length >= requiredCandles) {
      set({
        ohlcData: uniqueData,
        lastBatch: aggregatedData,
        loadingStatus: "complete",
        isReadyForChart: true,
        isRequestingMoreData: false,
        pendingBatchData: [], // Clear pending data
      });
    } else {
      set({
        ohlcData: uniqueData,
        pendingBatchData: uniquePendingData,
        loadingStatus:
          state.loadingStatus === "loading" ? "loading" : state.loadingStatus,
      });
    }
  },

  // Request initial OHLC data (last 5000 candles)
  requestOHLCInitializationData: async () => {
    const state = get();

    if (!state.selectedSymbol) {
      return;
    }

    try {
      set({ loadingStatus: "loading", isRequestingMoreData: true });

      const now = new Date();
      // Get full candle timestamp for current timeframe
      const toDate = getFullCandleTimestamp(now, state.selectedTimeframe);
      // Calculate from timestamp for 100 candles back
      const fromDate = calculateFromTimestamp(
        toDate,
        100,
        state.selectedTimeframe
      );

      // Import and send request
      const { VybeTraderClient } = await import(
        "../../connection/client/VybeTraderClient"
      );
      const client = VybeTraderClient.getInstance();

      if (!client.isConnected()) {
        throw new Error("Not connected to trading server");
      }

      const request: OHLCRequest = {
        symbol: state.selectedSymbol.currency,
        period: 0,
        customFrom: fromDate.toISOString(),
        customTo: toDate.toISOString(),
      };
      await client.send("GetOHLCData", request);
    } catch (error) {
      console.error("[Chart] Failed to request initialization data:", error);
      set({
        loadingStatus: "error",
        errorMessage:
          error instanceof Error ? error.message : "Failed to load chart data",
        isRequestingMoreData: false,
      });
    }
  },

  // Request more historical OHLC data
  requestMoreOHLCData: async (fromTimestamp: number) => {
    const state = get();

    if (!state.selectedSymbol) {
      return;
    }

    if (state.isRequestingMoreData) {
      return;
    }

    try {
      set({ isRequestingMoreData: true });

      // First check if we have older data in ohlcData
      const olderData = state.ohlcData.filter(
        (item) => item.timestamp < fromTimestamp
      );

      if (olderData.length > 0) {
        // Sort by timestamp descending to get the most recent older data first
        olderData.sort((a, b) => b.timestamp - a.timestamp);

        // Use efficient data extraction for aggregation
        const timeframedData = getDataForAggregation(
          olderData,
          state.selectedTimeframe,
          100
        );

        if (timeframedData.length > 0) {
          // Sort back to ascending order for aggregation
          timeframedData.sort((a, b) => a.timestamp - b.timestamp);

          // Aggregate the data based on current timeframe
          const aggregatedData = aggregateOHLCData(
            timeframedData,
            state.selectedTimeframe
          );

          if (aggregatedData.length > 0) {
            // We have at least one full candle, set it as lastBatch
            set({
              lastBatch: aggregatedData,
              isReadyForChart: true,
              isRequestingMoreData: false,
            });

            // If we have less than 50 candles, request more from server
            if (aggregatedData.length < 50) {
              // Don't await, let it run in background
              get().requestMoreOHLCData(timeframedData[0].timestamp);
            }

            return;
          }
        }
      }

      // Calculate to timestamp (1 second before fromTimestamp)
      const toDate = new Date(fromTimestamp - 1000);
      const fromDate = calculateFromTimestamp(
        toDate,
        100,
        state.selectedTimeframe
      );

      // Import and send request
      const { VybeTraderClient } = await import(
        "../../connection/client/VybeTraderClient"
      );
      const client = VybeTraderClient.getInstance();

      if (!client.isConnected()) {
        throw new Error("Not connected to trading server");
      }

      const request: OHLCRequest = {
        symbol: state.selectedSymbol.currency,
        period: 0,
        customFrom: fromDate.toISOString(),
        customTo: toDate.toISOString(),
      };

      await client.send("GetOHLCData", request);
    } catch (error) {
      console.error("[Chart] Failed to request more data:", error);
      set({ isRequestingMoreData: false });
    }
  },

  // Get aggregated data for chart display - now optimized
  getAggregatedData: (timeframe?: TimeFrame) => {
    const state = get();
    const tf = timeframe || state.selectedTimeframe;

    if (state.ohlcData.length === 0) {
      return [];
    }

    // Use efficient data extraction
    const dataForAggregation = getDataForAggregation(state.ohlcData, tf, 500);

    if (tf === "1m") {
      return dataForAggregation;
    }

    const aggregatedData = aggregateOHLCData(dataForAggregation, tf);

    return aggregatedData;
  },
}));
